import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SettingsMailTemplatePage } from "./pages/SettingsMailTemplate.jsx";
import { MSG, type Me } from "@genbaryoku/contract";
import { useEffect, useLayoutEffect, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useParams } from "react-router";
import { ME_KEY, RedirectIfAuthed, RequireAuth, useMe } from "./auth.js";
import { applyTextSize, currentView } from "./lib/view.js";
import { PRELOGIN_PRODUCT } from "./lib/product.js";
import { applyMode, applyTheme, modeFor, subscribeSystemDark, systemDark, themeFor } from "./lib/theme.js";
import { Shell } from "./components/Shell.jsx";
import { NoticeHost, useNoticeScreen } from "./components/Notice.jsx";
import { ErrorText } from "./components/ui.jsx";
import { AdminOnlyStop, MaintenanceStop, NotFoundStop, PcOnlyStop, ReadOnlyStop } from "./components/StopScreen.jsx";
import { useMaintenance } from "./lib/maintenance.js";
import { usePcOnlyHere } from "./lib/pcOnly.js";
import { registeredRoutes, routeAllowed, type RouteEntry } from "./core/slots.jsx";
import { AccountPlanPage } from "./pages/AccountPlan.jsx";
import { AccountProfilePage } from "./pages/AccountProfile.jsx";
import { ForgotPage } from "./pages/Forgot.jsx";
import { EstimatePage } from "./pages/Estimate.jsx";
import { EstimateNewPage } from "./pages/EstimateNew.jsx";
import { EstimatesPage } from "./pages/Estimates.jsx";
import { PhoneEstimatePage } from "./pages/phone/PhoneEstimate.jsx";
import { PhoneEstimatesPage } from "./pages/phone/PhoneEstimates.jsx";
import { HomePage } from "./pages/Home.jsx";
import { InvoicePage } from "./pages/Invoice.jsx";
import { ReceiptPage } from "./pages/Receipt.jsx";
import { GroupPage } from "./pages/Group.jsx";
import { GroupNewPage } from "./pages/GroupNew.jsx";
import { GroupsPage } from "./pages/Groups.jsx";
import { ScheduleOverviewPage } from "./pages/schedule/Overview.jsx";
import { SchedulePage } from "./pages/schedule/Schedule.jsx";
import { SchedulePaperPage } from "./pages/schedule/SchedulePaper.jsx";
import { EstimateComparePage, OrderComparePage } from "./pages/EstimateCompare.jsx";
import { InvoicesPage } from "./pages/Invoices.jsx";
import { LoginPage } from "./pages/Login.jsx";
import { PartnersPage } from "./pages/Partners.jsx";
import { PartnerPage } from "./pages/Partner.jsx";
import { PartnerNewPage } from "./pages/PartnerNew.jsx";
import { ReopenPage } from "./pages/Reopen.jsx";
import { ResetPasswordPage } from "./pages/ResetPassword.jsx";
import { PaperPage } from "./pages/Paper.jsx";
import { SettingsCompanyPage } from "./pages/Settings.jsx";
import { SettingsBankAccountsPage } from "./pages/SettingsBankAccounts.jsx";
import { SettingsClosePage } from "./pages/SettingsClose.jsx";
import { SettingsMenuPage } from "./pages/SettingsMenu.jsx";
import { SettingsLayout } from "./components/SettingsLayout.jsx";
import { SettingsWordPage, SettingsWordsPage } from "./pages/SettingsWords.jsx";
import { SettingsUnitsPage } from "./pages/SettingsUnits.jsx";
import { SettingsRoundingPage } from "./pages/SettingsRounding.jsx";
import { SettingsEstimatePage, SettingsInstallmentsPage } from "./pages/SettingsEstimate.jsx";
import { SettingsRfqPage } from "./pages/SettingsRfq.jsx";
import { SettingsCoverEnvelopePage, SettingsCoverLetterPage, SettingsCoverLettersPage } from "./pages/SettingsCoverLetters.jsx";
import { CoverLetterNewPage } from "./pages/CoverLetterNew.jsx";
import { RfqPage } from "./pages/rfq/RfqPage.jsx";
import { RfqListPage } from "./pages/rfq/RfqListPage.jsx";
import { VendorQuoteDetailPage } from "./pages/vendorQuotes/Detail.jsx";
import { VendorQuoteListPage } from "./pages/vendorQuotes/List.jsx";
import { RfqPaperPage } from "./pages/rfq/RfqPaper.jsx";
import { RfqSendPage } from "./pages/rfq/RfqSend.jsx";
import { CoverLettersPage, DeliveryPage } from "./pages/CoverLetters.jsx";
import { SettingsFeaturesPage } from "./pages/SettingsFeatures.jsx";
import { WelcomePage } from "./pages/Welcome.jsx";
import { SettingsItemsPage, SettingsPlacesPage, SettingsTradesPage } from "./pages/SettingsAxes.jsx";
import { SettingsScheduleCalendarPage, SettingsScheduleTemplatesPage } from "./pages/SettingsSchedule.jsx";
import { SettingsPaperKindPage, SettingsPaperPage } from "./pages/SettingsPaper.jsx";
import { PaperEditorPage } from "./pages/paper/PaperEditor.jsx";
import { SettingsUsersPage } from "./pages/SettingsUsers.jsx";
import { SettingsLoginMethodsPage } from "./pages/SettingsLoginMethods.jsx";
import { OAuthContinuePage } from "./pages/OAuthContinue.jsx";
import { ChoosePage } from "./pages/Choose.jsx";
import { HandoffPage } from "./pages/Handoff.jsx";
import { InvitePage } from "./pages/Invite.jsx";
import { TemplatesPage } from "./pages/Templates.jsx";
import { TemplateEditPage } from "./pages/TemplateEdit.jsx";
import { SignupPage } from "./pages/Signup.jsx";
import { SignupSentPage } from "./pages/SignupSent.jsx";
import { TermsPage } from "./pages/Terms.jsx";
import { PrivacyPage } from "./pages/Privacy.jsx";
import { BackTracker } from "./components/BackButton.jsx";
import { CodeDialogHost } from "./components/CodeDialog.jsx";
import { AutoLogoutHost } from "./components/AutoLogout.jsx";
import { ErrorBoundary } from "./components/ErrorBoundary.jsx";
import { ReauthDialogHost } from "./components/ReauthDialog.jsx";
import { SettingsAutoLogoutPage } from "./pages/SettingsAutoLogout.jsx";
import { SettingsNumberingEditPage, SettingsNumberingPage } from "./pages/SettingsNumbering.jsx";
import { SettingsImportsPage } from "./pages/SettingsImports.jsx";
import { ImportNewPage, ImportPage } from "./pages/imports/ImportPage.jsx";
import { OAuthReauthPage } from "./pages/OAuthReauth.jsx";
import { TokushohoPage } from "./pages/Tokushoho.jsx";
import { VerifyPage } from "./pages/Verify.jsx";
import { VendorQuoteNewPage } from "./pages/VendorQuotes.jsx";
import { PartnerLinkPage } from "./pages/partner/PartnerPage.jsx";
import { DeliveryViewPage } from "./pages/delivery/DeliveryView.jsx";
import { OrderPage } from "./pages/orders/OrderPage.jsx";
import { OrderListPage } from "./pages/orders/OrderListPage.jsx";
import { OrderNewPage } from "./pages/orders/OrderNewPage.jsx";
import { OrderPaperPage } from "./pages/orders/OrderPaper.jsx";
import { OrderAcceptPage } from "./pages/orders/OrderAcceptPage.jsx";
import { SettingsOrdersPage } from "./pages/SettingsOrders.jsx";
import { PurchaseListPage } from "./pages/purchases/PurchaseListPage.jsx";
import { PurchaseNewPage } from "./pages/purchases/PurchaseNewPage.jsx";
import { PurchasePage } from "./pages/purchases/PurchasePage.jsx";
import { PaymentNoticeListPage } from "./pages/payment-notices/PaymentNoticeListPage.jsx";
import { DeliveryNoteListPage } from "./pages/delivery-notes/DeliveryNoteListPage.jsx";
import { ClosingPage } from "./pages/closing/ClosingPage.jsx";
import { RecurringPage } from "./pages/recurring/RecurringPage.jsx";
import { DeliveryNoteNewPage } from "./pages/delivery-notes/DeliveryNoteNewPage.jsx";
import { DeliveryNotePage } from "./pages/delivery-notes/DeliveryNotePage.jsx";
import { AcceptancePublicPage } from "./pages/delivery-notes/AcceptancePublicPage.jsx";
import { PaymentNoticeNewPage } from "./pages/payment-notices/PaymentNoticeNewPage.jsx";
import { PaymentNoticePage } from "./pages/payment-notices/PaymentNoticePage.jsx";
import { PaymentNoticePaperPage } from "./pages/payment-notices/PaymentNoticePaper.jsx";
import { NoticeConfirmPage } from "./pages/payment-notices/NoticeConfirmPage.jsx";
import { ContactPage } from "./pages/Contact.jsx";
import { UpdatesPage } from "./pages/Updates.jsx";
import { SupportThreadPage } from "./pages/SupportThread.jsx";
import { useReportCount } from "./pages/schedule/Reports.jsx";

const qc = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false } } });
// 文字の大きさ（#115）：ログインして Me に人の設定があれば、それを当てて端末の Cookie にも書く（ログイン・会社の切替・取り直しのたび）
qc.getQueryCache().subscribe((e) => {
  if (e.query.queryKey[0] !== ME_KEY[0]) return;
  const size = (e.query.state.data as Me | null | undefined)?.preferences?.textSize;
  if (size) applyTextSize(size);
});

/**
 * テーマ色（#191、docs/77）と明るさ（2回目、§8）：ME（人の設定・会社の製品）と開いている道から <html data-theme>・<html data-mode> を決める。描く前（useLayoutEffect）に当てる。
 * ログイン前の画面（lib/theme.ts の isPreloginPath）と ME の無いときは製品の既定＝人の設定を当てない
 */
function ThemeHost() {
  const me = useMe();
  const loc = useLocation();
  const theme = themeFor(me.data, loc.pathname, PRELOGIN_PRODUCT);
  // 明るさ（#191 の2回目）：人の設定・端末（端末に合わせるのとき OS の設定が変われば追従）・道（ログイン前・帳票のプレビューはライト）
  const sysDark = useSyncExternalStore(subscribeSystemDark, systemDark, () => false);
  const mode = modeFor(me.data, loc.pathname, sysDark);
  useLayoutEffect(() => {
    applyTheme(theme);
    applyMode(mode);
  }, [theme, mode]);
  return null;
}

/**
 * 氏名がそろっていない人（#152、docs/61 ★e）：ログインの枠の中の画面（Shell）を開く前に「はじめに」（/welcome）へ。飛ばせない。
 * 会員登録で氏名を聞くので、来るのは Google・Apple から氏名が届かなかった人と、氏名を入れずに使ってきた人（★d で埋まらなかった人）
 */
function NeedsName({ me, children }: { me: Me; children: React.ReactNode }) {
  if (me.account.needsName) return <Navigate to="/welcome" replace />;
  return <>{children}</>;
}

/** 前の道から新しい道へ（? のあと＝Google・Apple から戻った印 ?oauth_linked などはそのまま） */
function MovedTo({ to }: { to: string }) {
  const loc = useLocation();
  return <Navigate to={to + loc.search} replace />;
}

/** #198：外注見積の登録は案件を任意に（/vendor-quotes/new?group=）。前の道 /groups/:id/vendor-quotes/new から */
function VendorQuoteNewMoved() {
  const { id = "" } = useParams();
  return <Navigate to={`/vendor-quotes/new?group=${encodeURIComponent(id)}`} replace />;
}

/**
 * 止めの画面（#186、docs/75。components/StopScreen.tsx）をルートの共通の守りで出す。画面の中身は描かない（API も呼ばない）
 *   a) PC 専用の機能（contract の PC_ONLY_FEATURES の道）をスマホ（スマホ版・幅 767px 以下）で開いた →「パソコンから操作してください」
 *   f) 管理者だけの画面（ADMIN_ONLY_PAGES）を担当者が開いた →「この操作には管理者の権限が必要です」
 *   c) 無料トライアルが終わり閲覧のみの会社が、作成の画面（CREATE_PAGES）を開いた →「閲覧のみの状態です」
 *   どれも URL を直接開いたとき（メールのリンク・ブックマーク・入力）の守り。メニュー・入口はもともと出さない（pcOnly・設定のメニュー）
 */
/** 担当者が開くと f) にする画面：ユーザー管理（招待・修正のシートも）と退会。ほかの設定の画面は担当者も閲覧できる（押せない形） */
const ADMIN_ONLY_PAGES: ReadonlySet<string> = new Set(["settings-users", "settings-close", "settings-auto-logout", "settings-imports", "import-new", "import"]);
/** 閲覧のみの会社が開くと c) にする作成の画面（API も 402 BILLING_READ_ONLY で断る） */
const CREATE_PAGES: ReadonlySet<string> = new Set(["estimate-new", "group-new", "partner-new", "vendor-quote-new", "order-new", "delivery-note-new"]);

function stopFor(me: Me, page: string | null, pcHidden: boolean): React.ReactNode | null {
  if (pcHidden) return <PcOnlyStop />;
  if (page && ADMIN_ONLY_PAGES.has(page) && me.user.role !== "admin") return <AdminOnlyStop />;
  if (page && CREATE_PAGES.has(page) && me.billing.readOnly) return <ReadOnlyStop admin={me.user.role === "admin"} />;
  return null;
}

/**
 * e) メンテナンス中（API の 503 MAINTENANCE、lib/maintenance.ts）：画面全体に止めの画面を重ねる。下の画面は外さず、操作できなくするだけ（inert）。
 *   戻すのは「再読み込み」だけ（送れていない直しがあれば、ページを離れる前にブラウザが確かめる）
 */
function MaintenanceGate() {
  const m = useMaintenance();
  useEffect(() => {
    const root = document.getElementById("root");
    if (!root || !m.on) return;
    root.inert = true;
    return () => {
      root.inert = false;
    };
  }, [m.on]);
  if (!m.on) return null;
  return createPortal(
    <div className="stop-over" role="alert" aria-live="assertive">
      <MaintenanceStop endsAt={m.endsAt} note={m.note} overdue={m.overdue} />
    </div>,
    document.body,
  );
}

/**
 * lineEntry：明細を入力する画面（#169、docs/10 §9.21）＝左のメニューを畳んで始める（見積書・請求書・{vendorQuote}の取込。#188 で見積書の比較も＝明細を並べる画面。#197 で雛形の編集も。#217 で帳票デザインのエディタも）。
 * 発注書・納品書など明細の画面を足すときは、その Route の Authed（製品の置き場の画面は registerRoute の lineEntry）に付ける
 */
function Authed({ page, lineEntry = false }: { lineEntry?: boolean; page: "home" | "estimates" | "estimate-new" | "estimate" | "paper" | "invoices" | "invoice" | "invoice-paper" | "receipt" | "receipt-paper" | "group" | "groups" | "group-new" | "estimate-compare" | "partners" | "partner-new" | "partner" | "settings" | "settings-company" | "settings-bank-accounts" | "settings-auto-logout" | "settings-close" | "settings-words" | "settings-word" | "settings-units" | "settings-rounding" | "settings-numbering" | "settings-numbering-edit" | "settings-imports" | "import-new" | "import" | "settings-estimate" | "settings-estimate-installments" | "settings-mail-template" | "settings-features" | "settings-trades" | "settings-places" | "settings-items" | "settings-schedule-calendar" | "settings-schedule-templates" | "settings-paper" | "settings-paper-kind" | "paper-edit" | "settings-users" | "account-login-methods" | "templates" | "template" | "account-profile" | "account-plan" | "vendor-quote-new" | "vendor-quote" | "vendor-quotes" | "rfq" | "rfq-paper" | "rfqs" | "settings-rfq" | "settings-cover-letters" | "settings-cover-letter" | "settings-cover-envelope" | "cover-letter-new" | "cover-letters" | "delivery" | "rfq-send" | "schedule" | "schedule-overview" | "schedule-paper" | "schedule-loose-paper" | "schedule-overview-paper" | "orders" | "order-new" | "order" | "order-paper" | "order-compare" | "settings-orders" | "purchases" | "purchase-new" | "purchase" | "payment-notices" | "payment-notice-new" | "payment-notice" | "payment-notice-paper" | "delivery-notes" | "closing" | "recurring" | "delivery-note-new" | "delivery-note" | "delivery-note-paper" | "acceptance" | "acceptance-paper" }) {
  const me = useMe();
  // メニュー「工程表」の朱の丸：協力会社からの報告の確かめ待ち（#103。工程管理が使える会社だけ）
  const reports = useReportCount(!!me.data?.features.schedule);
  // 通知（#168）：画面が変わったら前の画面の知らせを消す（同じ page の窓・シートの URL では消さない）
  useNoticeScreen(page);
  const pcHidden = usePcOnlyHere();
  if (!me.data) return null; // RequireAuth が先に見ている
  const stop = stopFor(me.data, page, pcHidden);
  if (stop) return <NeedsName me={me.data}>{stop}</NeedsName>;
  const phone = currentView() === "phone";
  const body =
    page === "home" ? <HomePage me={me.data} />
    : page === "partners" ? <PartnersPage />
    : page === "partner-new" ? <PartnerNewPage />
    : page === "partner" ? <PartnerPage />
    // スマホ版（#117、docs/44 §9）：見積の一覧と見積の土台はスマホの画面（pages/phone/）。PC版の画面・URL はそのまま
    : page === "estimates" ? (phone ? <PhoneEstimatesPage /> : <EstimatesPage />)
    : page === "estimate" ? (phone ? <PhoneEstimatePage /> : <EstimatePage />)
    : page === "settings" ? <SettingsMenuPage />
    : page === "settings-company" ? <SettingsCompanyPage />
    : page === "settings-bank-accounts" ? <SettingsBankAccountsPage />
    : page === "settings-auto-logout" ? <SettingsAutoLogoutPage />
    : page === "settings-close" ? <SettingsClosePage />
    : page === "settings-words" ? <SettingsWordsPage />
    : page === "settings-word" ? <SettingsWordPage />
    : page === "settings-units" ? <SettingsUnitsPage />
    : page === "settings-rounding" ? <SettingsRoundingPage />
    : page === "settings-numbering" ? <SettingsNumberingPage />
    : page === "settings-numbering-edit" ? <SettingsNumberingEditPage />
    : page === "settings-imports" ? <SettingsImportsPage />
    : page === "import-new" ? <ImportNewPage />
    : page === "import" ? <ImportPage />
    : page === "settings-estimate" ? <SettingsEstimatePage />
    : page === "settings-estimate-installments" ? <SettingsInstallmentsPage />
    : page === "settings-mail-template" ? <SettingsMailTemplatePage />
    : page === "settings-rfq" ? <SettingsRfqPage />
    : page === "settings-orders" ? <SettingsOrdersPage />
    : page === "orders" ? <OrderListPage />
    : page === "order-new" ? <OrderNewPage />
    : page === "order" ? <OrderPage />
    : page === "order-paper" ? <OrderPaperPage />
    : page === "order-compare" ? <OrderComparePage />
    : page === "purchases" ? <PurchaseListPage />
    : page === "purchase-new" ? <PurchaseNewPage />
    : page === "purchase" ? <PurchasePage />
    : page === "payment-notices" ? <PaymentNoticeListPage />
    : page === "payment-notice-new" ? <PaymentNoticeNewPage />
    : page === "payment-notice" ? <PaymentNoticePage />
    : page === "payment-notice-paper" ? <PaymentNoticePaperPage />
    : page === "delivery-notes" ? <DeliveryNoteListPage />
    : page === "closing" ? <ClosingPage />
    : page === "recurring" ? <RecurringPage />
    : page === "delivery-note-new" ? <DeliveryNoteNewPage />
    : page === "delivery-note" ? <DeliveryNotePage />
    : page === "delivery-note-paper" ? <PaperPage kind="delivery_note" />
    : page === "acceptance" ? <DeliveryNotePage kind="acceptance" />
    : page === "acceptance-paper" ? <PaperPage kind="acceptance" />
    : page === "settings-cover-letters" ? <SettingsCoverLettersPage />
    : page === "settings-cover-letter" ? <SettingsCoverLetterPage />
    : page === "settings-cover-envelope" ? <SettingsCoverEnvelopePage />
    : page === "cover-letter-new" ? <CoverLetterNewPage />
    : page === "cover-letters" ? <CoverLettersPage />
    : page === "delivery" ? <DeliveryPage />
    : page === "rfq-send" ? <RfqSendPage />
    : page === "rfq" ? <RfqPage />
    : page === "rfq-paper" ? <RfqPaperPage />
    : page === "rfqs" ? <RfqListPage />
    : page === "settings-features" ? <SettingsFeaturesPage />
    : page === "settings-trades" ? <SettingsTradesPage />
    : page === "settings-places" ? <SettingsPlacesPage />
    : page === "settings-items" ? <SettingsItemsPage />
    : page === "settings-schedule-calendar" ? <SettingsScheduleCalendarPage />
    : page === "settings-schedule-templates" ? <SettingsScheduleTemplatesPage />
    : page === "settings-paper" ? <SettingsPaperPage />
    : page === "settings-paper-kind" ? <SettingsPaperKindPage />
    : page === "paper-edit" ? <PaperEditorPage />
    : page === "settings-users" ? <SettingsUsersPage />
    : page === "account-login-methods" ? <SettingsLoginMethodsPage />
    : page === "templates" ? <TemplatesPage />
    : page === "template" ? <TemplateEditPage />
    : page === "account-profile" ? <AccountProfilePage />
    : page === "account-plan" ? <AccountPlanPage />
    : page === "vendor-quote-new" ? <VendorQuoteNewPage />
    : page === "vendor-quote" ? <VendorQuoteDetailPage />
    : page === "vendor-quotes" ? <VendorQuoteListPage />
    : page === "paper" ? <PaperPage kind="quote" />
    : page === "invoices" ? <InvoicesPage />
    : page === "invoice" ? <InvoicePage />
    : page === "invoice-paper" ? <PaperPage kind="invoice" />
    : page === "receipt" ? <ReceiptPage />
    : page === "receipt-paper" ? <PaperPage kind="receipt" />
    : page === "group" ? <GroupPage />
    : page === "groups" ? <GroupsPage />
    : page === "group-new" ? <GroupNewPage />
    : page === "schedule" ? <SchedulePage />
    : page === "schedule-overview" ? <ScheduleOverviewPage />
    : page === "schedule-paper" ? <SchedulePaperPage kind="group" />
    : page === "schedule-loose-paper" ? <SchedulePaperPage kind="standalone" />
    : page === "schedule-overview-paper" ? <SchedulePaperPage kind="overview" />
    : page === "estimate-compare" ? <EstimateComparePage />
    : <EstimateNewPage />;
  // PC版の設定は2列（#153、docs/62 §6・★3）：左に設定のメニュー、右に選んだ画面。帳票デザインのエディタ（paper-edit）は全面のまま。
  // 雛形の編集（template）も見積書と同じく設定の列を出さない全幅（#197、docs/78。左のメニューは Route の lineEntry で畳んで始める）
  const settingsPage = page.startsWith("settings") || page === "templates";
  const inSettings = !phone && settingsPage;
  // スマホ版の設定・個人設定は左右の余白 8px（S-00009、docs/44 §10。styles.css の .setphone。幅で形が変わるだけ。雛形の編集も前のまま＝#197 で変えたのは PC の2列だけ）
  const setPhone = settingsPage || page === "template" || page === "account-profile" || page === "account-login-methods" || page === "account-plan";
  return (
    <NeedsName me={me.data}>
      <Shell me={me.data} badges={{ schedule: reports }} lineEntry={lineEntry} free={page === "paper-edit"} autoMin={page === "schedule" || page === "schedule-overview"}>{inSettings ? <SettingsLayout>{body}</SettingsLayout> : setPhone ? <div className="setphone">{body}</div> : body}</Shell>
    </NeedsName>
  );
}

/** 製品の置き場が登録した画面（営業力の入金など）。その会社でモジュールが OFF なら「プランに含まれていません」（API も 404 MODULE_OFF） */
function Registered({ entry }: { entry: RouteEntry }) {
  const me = useMe();
  const reports = useReportCount(!!me.data?.features.schedule);
  useNoticeScreen(entry.path);
  const pcHidden = usePcOnlyHere();
  if (!me.data) return null;
  const stop = stopFor(me.data, null, pcHidden);
  if (stop) return <NeedsName me={me.data}>{stop}</NeedsName>;
  const body = routeAllowed(me.data, entry) ? (
    entry.element()
  ) : (
    <div className="soonbox" data-testid="module-off">
      <ErrorText>{MSG.moduleOff}</ErrorText>
      <p>
        <Link className="btn text" to="/home">今日へ</Link>
      </p>
    </div>
  );
  return (
    <NeedsName me={me.data}>
      <Shell me={me.data} badges={{ schedule: reports }} lineEntry={!!entry.lineEntry}>{body}</Shell>
    </NeedsName>
  );
}

export function App() {
  return (
    <QueryClientProvider client={qc}>
      <BrowserRouter>
        <BackTracker />
        <ThemeHost />
        {/* 確認コードの窓（#110、docs/42）：海外の IP からのログイン・会員登録・属性変更で API が求めたときだけ開く */}
        <CodeDialogHost />
        {/* 本人確認の窓（#189、docs/76 ⑤）：重い設定で API が 403 REAUTH_REQUIRED を返したときだけ開く */}
        <ReauthDialogHost />
        {/* 自動ログアウト（#189）：期限の5分前の帯・切れたときの止めの画面 g・タブ復帰の確かめ */}
        <AutoLogoutHost />
        {/* 真っ白にしない（#189 A）：画面の例外・部品の読み込みの失敗は止めの画面 h（部品は1回だけ自動で読み直す） */}
        <ErrorBoundary>
        <Routes>
          <Route path="/login" element={<RedirectIfAuthed><LoginPage /></RedirectIfAuthed>} />
          <Route path="/signup" element={<RedirectIfAuthed><SignupPage /></RedirectIfAuthed>} />
          <Route path="/signup/sent" element={<SignupSentPage />} />
          <Route path="/verify" element={<VerifyPage />} />
          <Route path="/forgot" element={<ForgotPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/reopen" element={<ReopenPage />} />
          <Route path="/invite" element={<InvitePage />} />
          <Route path="/choose" element={<ChoosePage />} />
          {/* 製品をまたぐ会社の切替の受け口（#151、docs/60 ★1・★e）：Shell の外。ログインの有無で包まない（無ければ「入る」のボタン、別の人なら止める） */}
          <Route path="/handoff" element={<HandoffPage />} />
          <Route path="/oauth/continue" element={<OAuthContinuePage />} />
          {/* Google・Apple での本人確認から戻る小さな窓（#189）：Shell の外。ログインの有無で包まない（API が見る） */}
          <Route path="/oauth/reauth" element={<OAuthReauthPage />} />
          {/* はじめに（#152、docs/61 ★e）：氏名がそろっていない人・ログインの画面から Google・Apple で初めて入った人（業種）。前の業種の1枚（#142 段4）はここへ */}
          <Route path="/welcome" element={<RequireAuth><WelcomePage /></RequireAuth>} />
          <Route path="/welcome/industry" element={<MovedTo to="/welcome" />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/legal" element={<TokushohoPage />} />
          {/* 協力会社のページ（#103、docs/39 §6.3.1）：ログインのいらない別の枠（アプリの枠・メニューは出さない） */}
          <Route path="/p/:token" element={<PartnerLinkPage />} />
          {/* メールの閲覧ページ（#225、docs/91 §3.3）：受け取る人がログインせずに書類を表示・ダウンロードする（アプリの枠・メニューは出さない） */}
          <Route path="/d/:token" element={<DeliveryViewPage />} />
          {/* {order}の承諾ページ（#233、docs/93 §4.7）：{vendor}がログインせずに確かめて「承諾」を押す（アプリの枠・メニューは出さない） */}
          <Route path="/o/:token" element={<OrderAcceptPage />} />
          <Route path="/n/:token" element={<NoticeConfirmPage />} />
          <Route path="/a/:token" element={<AcceptancePublicPage />} />
          {/* お問い合わせ（#124 の 3a、docs/50 §2.1）：ログイン前でも後でも開ける。返事のリンクのページはログイン不要 */}
          <Route path="/contact" element={<ContactPage />} />
          {/* 更新情報（#204、docs/83 §5）：ログイン前でも読める */}
          <Route path="/updates" element={<UpdatesPage />} />
          <Route path="/support/t/:token" element={<SupportThreadPage />} />
          <Route path="/home" element={<RequireAuth><Authed page="home" /></RequireAuth>} />
          <Route path="/partners" element={<RequireAuth><Authed page="partners" /></RequireAuth>} />
          <Route path="/partners/new" element={<RequireAuth><Authed page="partner-new" /></RequireAuth>} />
          <Route path="/partners/:id" element={<RequireAuth><Authed page="partner" /></RequireAuth>} />
          <Route path="/groups" element={<RequireAuth><Authed page="groups" /></RequireAuth>} />
          <Route path="/groups/new" element={<RequireAuth><Authed page="group-new" /></RequireAuth>} />
          <Route path="/groups/:id" element={<RequireAuth><Authed page="group" /></RequireAuth>} />
          <Route path="/groups/:id/schedule" element={<RequireAuth><Authed page="schedule" /></RequireAuth>} />
          <Route path="/schedule" element={<RequireAuth><Authed page="schedule-overview" /></RequireAuth>} />
          <Route path="/groups/:id/schedule/paper" element={<RequireAuth><Authed page="schedule-paper" /></RequireAuth>} />
          {/* 案件なしの{schedule}（#291）。案件に紐づいたら /groups/:id/schedule へ転送 */}
          <Route path="/schedules/:sid" element={<RequireAuth><Authed page="schedule" /></RequireAuth>} />
          <Route path="/schedules/:sid/paper" element={<RequireAuth><Authed page="schedule-loose-paper" /></RequireAuth>} />
          <Route path="/schedule/paper" element={<RequireAuth><Authed page="schedule-overview-paper" /></RequireAuth>} />
          <Route path="/groups/:id/vendor-quotes/new" element={<VendorQuoteNewMoved />} />
          {/* 外注見積の一覧（#198、docs/79）：案件をまたいで見る。案件の画面のタブからは ?group=&via=group で開く */}
          <Route path="/vendor-quotes" element={<RequireAuth><Authed page="vendor-quotes" /></RequireAuth>} />
          <Route path="/vendor-quotes/new" element={<RequireAuth><Authed page="vendor-quote-new" /></RequireAuth>} />
          <Route path="/vendor-quotes/:id" element={<RequireAuth><Authed page="vendor-quote" lineEntry /></RequireAuth>} />
          <Route path="/estimates" element={<RequireAuth><Authed page="estimates" /></RequireAuth>} />
          <Route path="/estimates/new" element={<RequireAuth><Authed page="estimate-new" /></RequireAuth>} />
          <Route path="/estimates/:id" element={<RequireAuth><Authed page="estimate" lineEntry /></RequireAuth>} />
          <Route path="/estimates/:id/paper" element={<RequireAuth><Authed page="paper" /></RequireAuth>} />
          <Route path="/estimates/:id/compare" element={<RequireAuth><Authed page="estimate-compare" lineEntry /></RequireAuth>} />
          <Route path="/invoices" element={<RequireAuth><Authed page="invoices" /></RequireAuth>} />
          {/* 締め請求書（#267 3a、docs/97）：月ごとの{customer}の締め。左のメニューに項目は足さない（印は請求書） */}
          <Route path="/closing" element={<RequireAuth><Authed page="closing" /></RequireAuth>} />
          <Route path="/recurring" element={<RequireAuth><Authed page="recurring" /></RequireAuth>} />
          <Route path="/invoices/:id" element={<RequireAuth><Authed page="invoice" lineEntry /></RequireAuth>} />
          <Route path="/invoices/:id/paper" element={<RequireAuth><Authed page="invoice-paper" /></RequireAuth>} />
          <Route path="/receipts/:id" element={<RequireAuth><Authed page="receipt" /></RequireAuth>} />
          <Route path="/receipts/:id/paper" element={<RequireAuth><Authed page="receipt-paper" /></RequireAuth>} />
          <Route path="/settings" element={<RequireAuth><Authed page="settings" /></RequireAuth>} />
          <Route path="/settings/company" element={<RequireAuth><Authed page="settings-company" /></RequireAuth>} />
          <Route path="/settings/bank-accounts" element={<RequireAuth><Authed page="settings-bank-accounts" /></RequireAuth>} />
          {/* #155（docs/64 §3）：口座の追加・修正は一覧の上のシート＝3つの URL とも一覧の画面（シートを開け閉めしても一覧は描き直さない） */}
          <Route path="/settings/bank-accounts/new" element={<RequireAuth><Authed page="settings-bank-accounts" /></RequireAuth>} />
          <Route path="/settings/bank-accounts/:id" element={<RequireAuth><Authed page="settings-bank-accounts" /></RequireAuth>} />
          {/* 自動ログアウト（#189、docs/76 ①）。管理者だけ（担当者は止めの画面 f） */}
          <Route path="/settings/auto-logout" element={<RequireAuth><Authed page="settings-auto-logout" /></RequireAuth>} />
          <Route path="/settings/close" element={<RequireAuth><Authed page="settings-close" /></RequireAuth>} />
          <Route path="/settings/words" element={<RequireAuth><Authed page="settings-words" /></RequireAuth>} />
          {/* 言葉の1語の画面（#156、docs/65 §7） */}
          <Route path="/settings/words/:key" element={<RequireAuth><Authed page="settings-word" /></RequireAuth>} />
          {/* 単位（#164、docs/66） */}
          <Route path="/settings/units" element={<RequireAuth><Authed page="settings-units" /></RequireAuth>} />
          <Route path="/settings/rounding" element={<RequireAuth><Authed page="settings-rounding" /></RequireAuth>} />
          <Route path="/settings/numbering" element={<RequireAuth><Authed page="settings-numbering" /></RequireAuth>} />
          <Route path="/settings/numbering/:docType" element={<RequireAuth><Authed page="settings-numbering-edit" /></RequireAuth>} />
          <Route path="/settings/imports" element={<RequireAuth><Authed page="settings-imports" /></RequireAuth>} />
          <Route path="/imports/new" element={<RequireAuth><Authed page="import-new" /></RequireAuth>} />
          <Route path="/imports/:id" element={<RequireAuth><Authed page="import" /></RequireAuth>} />
          <Route path="/settings/estimate" element={<RequireAuth><Authed page="settings-estimate" /></RequireAuth>} />
          <Route path="/settings/estimate/installments" element={<RequireAuth><Authed page="settings-estimate-installments" /></RequireAuth>} />
          {/* メールの文面（#225、docs/91 §3.5）：書類の種類ごと（quote・invoice・receipt・rfq） */}
          <Route path="/settings/estimate/mail/:kind" element={<RequireAuth><Authed page="settings-mail-template" /></RequireAuth>} />
          <Route path="/settings/rfq" element={<RequireAuth><Authed page="settings-rfq" /></RequireAuth>} />
          {/* #233：{order}（名称・電子承諾・約款・変更協議・印字項目） */}
          <Route path="/settings/orders" element={<RequireAuth><Authed page="settings-orders" /></RequireAuth>} />
          {/* 送付状（#223、docs/89 §4・§5.3）：設定（テンプレート・会社の設定）と単体送付状（PC だけ） */}
          <Route path="/settings/cover-letters" element={<RequireAuth><Authed page="settings-cover-letters" /></RequireAuth>} />
          <Route path="/settings/cover-letters/envelope" element={<RequireAuth><Authed page="settings-cover-envelope" /></RequireAuth>} />
          <Route path="/settings/cover-letters/:id" element={<RequireAuth><Authed page="settings-cover-letter" /></RequireAuth>} />
          <Route path="/cover-letters/new" element={<RequireAuth><Authed page="cover-letter-new" /></RequireAuth>} />
          {/* 左のメニュー「送付状」と送付の記録の PDF の画面（#223 の3回目の前の直し＝★12・★18） */}
          <Route path="/cover-letters" element={<RequireAuth><Authed page="cover-letters" /></RequireAuth>} />
          <Route path="/deliveries/:id" element={<RequireAuth><Authed page="delivery" /></RequireAuth>} />
          {/* #233：{order}（発注書）と{orderAck}の電子承諾。明細を入力する画面なので lineEntry */}
          <Route path="/orders" element={<RequireAuth><Authed page="orders" /></RequireAuth>} />
          <Route path="/orders/new" element={<RequireAuth><Authed page="order-new" /></RequireAuth>} />
          <Route path="/orders/:id" element={<RequireAuth><Authed page="order" lineEntry /></RequireAuth>} />
          <Route path="/orders/:id/paper" element={<RequireAuth><Authed page="order-paper" /></RequireAuth>} />
          {/* 仕入（#233 3a、docs/93 §15.10）：一覧・登録（PC だけ）・作成画面（明細を入力する画面＝lineEntry） */}
          <Route path="/purchases" element={<RequireAuth><Authed page="purchases" /></RequireAuth>} />
          <Route path="/purchases/new" element={<RequireAuth><Authed page="purchase-new" /></RequireAuth>} />
          <Route path="/purchases/:id" element={<RequireAuth><Authed page="purchase" lineEntry /></RequireAuth>} />
          <Route path="/payment-notices" element={<RequireAuth><Authed page="payment-notices" /></RequireAuth>} />
          <Route path="/payment-notices/new" element={<RequireAuth><Authed page="payment-notice-new" /></RequireAuth>} />
          <Route path="/payment-notices/:id" element={<RequireAuth><Authed page="payment-notice" lineEntry /></RequireAuth>} />
          <Route path="/payment-notices/:id/paper" element={<RequireAuth><Authed page="payment-notice-paper" /></RequireAuth>} />
          <Route path="/delivery-notes" element={<RequireAuth><Authed page="delivery-notes" /></RequireAuth>} />
          <Route path="/delivery-notes/new" element={<RequireAuth><Authed page="delivery-note-new" /></RequireAuth>} />
          <Route path="/delivery-notes/:id" element={<RequireAuth><Authed page="delivery-note" lineEntry /></RequireAuth>} />
          <Route path="/delivery-notes/:id/paper" element={<RequireAuth><Authed page="delivery-note-paper" /></RequireAuth>} />
          {/* 検収書（#266 1b）：納品書の双子（同じ画面・同じ紙の画面）。一覧は /delivery-notes?tab=acceptance */}
          <Route path="/acceptances/:id" element={<RequireAuth><Authed page="acceptance" lineEntry /></RequireAuth>} />
          <Route path="/acceptances/:id/paper" element={<RequireAuth><Authed page="acceptance-paper" /></RequireAuth>} />
          {/* #233 2b：{order}の比較（見積書の比較と同じ画面・明細の画面なので lineEntry） */}
          <Route path="/orders/:id/compare" element={<RequireAuth><Authed page="order-compare" lineEntry /></RequireAuth>} />
          <Route path="/rfqs" element={<RequireAuth><Authed page="rfqs" /></RequireAuth>} />
          {/* #218（docs/88 §4.2）：見積依頼書の作成画面は見積書と同じ形＝明細を入力する画面（左のメニューは 64px に畳んで始める） */}
          <Route path="/rfqs/:id" element={<RequireAuth><Authed page="rfq" lineEntry /></RequireAuth>} />
          <Route path="/rfqs/:id/paper" element={<RequireAuth><Authed page="rfq-paper" /></RequireAuth>} />
          {/* 見積依頼の送付の画面（★16）：印刷・FAX の「送付」・メールで送付状がオンのとき */}
          <Route path="/rfqs/:id/send" element={<RequireAuth><Authed page="rfq-send" /></RequireAuth>} />
          <Route path="/settings/features" element={<RequireAuth><Authed page="settings-features" /></RequireAuth>} />
          <Route path="/settings/trades" element={<RequireAuth><Authed page="settings-trades" /></RequireAuth>} />
          <Route path="/settings/places" element={<RequireAuth><Authed page="settings-places" /></RequireAuth>} />
          <Route path="/settings/items" element={<RequireAuth><Authed page="settings-items" /></RequireAuth>} />
          <Route path="/settings/schedule-calendar" element={<RequireAuth><Authed page="settings-schedule-calendar" /></RequireAuth>} />
          <Route path="/settings/schedule-templates" element={<RequireAuth><Authed page="settings-schedule-templates" /></RequireAuth>} />
          <Route path="/settings/paper" element={<RequireAuth><Authed page="settings-paper" /></RequireAuth>} />
          {/* 帳票デザインのエディタ（#217 ■3）：雛形の編集（#197）と同じく左のメニューを畳んで始める（lineEntry。出ると入る前の幅に戻る） */}
          <Route path="/settings/paper/edit" element={<RequireAuth><Authed page="paper-edit" lineEntry /></RequireAuth>} />
          {/* 帳票ごとの画面（#156、docs/65 §8）：quote・invoice・receipt */}
          <Route path="/settings/paper/:kind" element={<RequireAuth><Authed page="settings-paper-kind" /></RequireAuth>} />
          <Route path="/settings/users" element={<RequireAuth><Authed page="settings-users" /></RequireAuth>} />
          {/* 招待・修正は一覧の上のシート（#156、docs/65 §5。#155 の振込口座と同じ作り） */}
          <Route path="/settings/users/new" element={<RequireAuth><Authed page="settings-users" /></RequireAuth>} />
          <Route path="/settings/users/:id" element={<RequireAuth><Authed page="settings-users" /></RequireAuth>} />
          {/* ログイン方法は個人設定へ（#152 ★3）。前の道は転送 */}
          <Route path="/settings/login-methods" element={<MovedTo to="/account/login-methods" />} />
          <Route path="/settings/templates" element={<RequireAuth><Authed page="templates" /></RequireAuth>} />
          {/* 雛形の編集（#197）：明細を入力する画面＝lineEntry（左のメニューを畳んで始める）。URL は設定の下のまま */}
          <Route path="/settings/templates/:id" element={<RequireAuth><Authed page="template" lineEntry /></RequireAuth>} />
          <Route path="/account/profile" element={<RequireAuth><Authed page="account-profile" /></RequireAuth>} />
          <Route path="/account/plan" element={<RequireAuth><Authed page="account-plan" /></RequireAuth>} />
          <Route path="/account/login-methods" element={<RequireAuth><Authed page="account-login-methods" /></RequireAuth>} />
          {registeredRoutes().map((r) => (
            <Route key={r.path} path={r.path} element={<RequireAuth><Registered entry={r} /></RequireAuth>} />
          ))}
          {/* 開発の / はホームへ（本番の / は紹介ページ＝Caddy）。どのルートにも当たらない道は d)「ページが見つかりません」（#186。前はホームを出していた） */}
          <Route path="/" element={<Navigate to="/home" replace />} />
          <Route path="*" element={<NotFoundStop />} />
        </Routes>
        </ErrorBoundary>
        {/* e) メンテナンス中（#186）：API が 503 MAINTENANCE を返したら画面全体に重ねる */}
        <MaintenanceGate />
        {/* 通知の置き場（#168、components/Notice.tsx）：右下に3つまで。前の画面からの一言（state.notice）もここで出す */}
        <NoticeHost />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
